import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";
import { Sparkles, Inbox, ChevronLeft, ChevronRight, X } from "lucide-react";
import {
  currentMonthIST,
  formatISTDate,
  formatISTTime,
  monthLabelIST,
  monthRangeIST,
} from "../../lib/datetime";
import { attendanceLabel, count, NO_RECORD_HELP, NO_VALUE } from "../../lib/vocabulary";
import StickyHeading from "../../components/common/StickyHeading";
import { StatusPill } from "./MyAttendancePage";

const ROLE_TAG = { sales_officer: "SO", assistant_manager: "Manager", admin_manager: "Manager" };

/**
 * Green above 90, amber 75–90, red below. The point of the page is to make the
 * person to worry about obvious without reading every number.
 *
 * NOTHING TO RATE IS NOT A BAD RATE. A month with no working days — a future
 * month, or one whose calendar has not been set up — used to paint 0% in red
 * against every employee. Red is a judgement and there is nothing to judge.
 */
function pctClass(pct, workingDays) {
  if (!workingDays || pct === null || pct === undefined) return "text-callisto-muted";
  if (pct >= 90) return "text-emerald-700";
  if (pct >= 75) return "text-amber-700";
  return "text-red-700";
}

export default function AllAttendancePage() {
  const start = currentMonthIST();
  const [year, setYear] = useState(start.year);
  const [month, setMonth] = useState(start.month);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // The raw day-by-day rows, one person at a time. This page used to open
  // straight onto every employee for every day; that view still answers "why is
  // Nitin 18 of 26?", so it is kept as a drill-down rather than removed.
  const [drill, setDrill] = useState(null); // { user_id, full_name }
  const [drillRows, setDrillRows] = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillError, setDrillError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api.get("/attendance/monthly-summary", { params: { year, month } })
      .then(({ data }) => !cancelled && setSummary(data))
      .catch((e) => !cancelled && setError(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [year, month]);

  useEffect(() => {
    if (!drill) return;
    let cancelled = false;
    setDrillLoading(true);
    setDrillError("");
    const { first, last } = monthRangeIST(year, month);
    api.get("/attendance", {
      params: { so_id: drill.user_id, from_date: first, to_date: last },
    })
      .then(({ data }) => !cancelled && setDrillRows(data))
      .catch((e) => !cancelled && setDrillError(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => !cancelled && setDrillLoading(false));
    return () => { cancelled = true; };
  }, [drill, year, month]);

  function shift(delta) {
    let m = month + delta;
    let y = year;
    if (m < 1) { m = 12; y -= 1; } else if (m > 12) { m = 1; y += 1; }
    setYear(y);
    setMonth(m);
    setDrill(null);
  }

  const rows = summary?.rows || [];

  return (
    <div className="space-y-5" data-testid="all-attendance-page">
      <StickyHeading
        dense
        icon={Sparkles}
        label="Company Attendance"
        title={monthLabelIST(year, month)}
        subtitle={
          loading
            ? "Loading…"
            : `${rows.length} ${rows.length === 1 ? "employee" : "employees"} · ${summary?.working_days ?? 0} working days`
        }
        testId="all-attendance-heading"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => shift(-1)} className="h-9 w-9 p-0 border-callisto" data-testid="attendance-prev-month">
              <ChevronLeft size={15} />
            </Button>
            <Button variant="outline" onClick={() => shift(1)} className="h-9 w-9 p-0 border-callisto" data-testid="attendance-next-month">
              <ChevronRight size={15} />
            </Button>
          </div>
        }
      />

      {error && <div className="rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2">{error}</div>}

      {loading ? (
        <Skeleton className="h-64 rounded-xl" />
      ) : rows.length === 0 && !error ? (
        <div className="bg-white border border-callisto rounded-xl p-10 flex flex-col items-center text-center" data-testid="all-attendance-empty">
          <div className="h-14 w-14 rounded-xl bg-callisto-navy/5 flex items-center justify-center mb-3">
            <Inbox className="h-6 w-6 text-callisto-navy" />
          </div>
          <p className="text-sm font-semibold text-callisto-navy">Nothing to show for this month</p>
        </div>
      ) : (
        <div className="bg-white border border-callisto rounded-xl overflow-x-auto" data-testid="all-attendance-summary">
          <table className="w-full text-sm min-w-[560px]">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-callisto-muted border-b border-callisto">
                <th className="text-left font-semibold px-4 py-2.5">Employee</th>
                <th className="text-right font-semibold px-3 py-2.5">{attendanceLabel("Present")}</th>
                <th className="text-right font-semibold px-3 py-2.5">{attendanceLabel("On Leave")}</th>
                <th className="text-right font-semibold px-3 py-2.5">{attendanceLabel("Absent")}</th>
                <th className="text-right font-semibold px-4 py-2.5">Check-in rate</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const rateKnown =
                  Boolean(r.working_days) && r.present_pct !== null && r.present_pct !== undefined;
                return (
                  <tr
                    key={r.user_id}
                    onClick={() => setDrill({ user_id: r.user_id, full_name: r.full_name })}
                    className="border-b border-callisto/50 last:border-0 cursor-pointer hover:bg-callisto-app/50"
                    data-testid={`attendance-row-${r.user_id}`}
                  >
                    <td className="px-4 py-2.5">
                      <span className="font-semibold text-callisto-navy">{r.full_name}</span>
                      <span className="ml-1.5 text-[10px] text-callisto-muted">{ROLE_TAG[r.role]}</span>
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums">
                      <span className="font-bold text-callisto-navy">{count(r.present)}</span>
                      <span className="text-callisto-muted"> of {count(r.working_days)}</span>
                    </td>
                    {/* `{r.on_leave || "—"}` printed a dash for a real 0: a person
                        who took no leave read as a person whose leave is unknown. */}
                    <td className="px-3 py-2.5 text-right tabular-nums text-callisto-muted">{count(r.on_leave)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-callisto-muted">{count(r.absent)}</td>
                    <td className={`px-4 py-2.5 text-right tabular-nums font-bold ${pctClass(r.present_pct, r.working_days)}`}>
                      {rateKnown ? `${r.present_pct}%` : NO_VALUE}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {/* The column is a SUBTRACTION the backend performs — working days
              minus days with a Present or On leave record — so it counts days
              nobody wrote anything for. Said in the open, where the number is. */}
          <p className="px-4 py-2.5 text-[11px] text-callisto-muted border-t border-callisto" data-testid="all-attendance-footnote">
            “{attendanceLabel("Absent")}” counts working days with no Present or on-leave record. {NO_RECORD_HELP}
          </p>
        </div>
      )}

      {drill && (
        <section className="bg-white border border-callisto rounded-xl" data-testid="attendance-drilldown">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-callisto">
            <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-callisto-gold">
              {drill.full_name}
            </span>
            <span className="text-xs text-callisto-muted">day by day · {monthLabelIST(year, month)}</span>
            <button
              type="button"
              onClick={() => setDrill(null)}
              className="ml-auto text-callisto-muted hover:text-callisto-navy"
              aria-label="Close day-by-day view"
              data-testid="attendance-drilldown-close"
            >
              <X size={16} />
            </button>
          </div>
          {drillError && (
            <div className="m-3 rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2">{drillError}</div>
          )}
          {drillLoading ? (
            <Skeleton className="h-32 m-3 rounded-lg" />
          ) : drillRows.length === 0 && !drillError ? (
            // Was: "No records this month — every working day counts as absent."
            // The app cannot know that. It knows only that nothing was written.
            <p className="px-4 py-6 text-center text-xs text-callisto-muted" data-testid="attendance-drilldown-empty">
              No attendance was captured for {drill.full_name} in {monthLabelIST(year, month)}. {NO_RECORD_HELP}
            </p>
          ) : (
            <div className="divide-y divide-callisto">
              {drillRows.map((r) => (
                <div key={r.id} className="flex items-center justify-between gap-3 px-4 py-2" data-testid={`attendance-drilldown-row-${r.date}`}>
                  <p className="text-[11px] text-callisto-muted">
                    {formatISTDate(r.date)}
                    {r.check_in_time && <> · in {formatISTTime(r.check_in_time)}</>}
                    {r.check_out_time && <> · out {formatISTTime(r.check_out_time)} · {r.working_hours}h</>}
                  </p>
                  <StatusPill status={r.status} testId={`attendance-drilldown-status-${r.date}`} />
                </div>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
